import { Validators } from '../../../../../../utils/validators';
import { FormModel } from '../../../../../../components/Form';
import { ReactNode } from 'react';

const sectionHeader = (label: string, field: string): FormModel => ({
  label,
  value: null,
  type: 'section',
  placeholder: '',
  field,
  validators: [],
  required: false,
  responsive: { xs: 12 },
});

const serviceChargeField = (strings: any, serviceChargeDropdown: boolean): FormModel => {
  if (serviceChargeDropdown) {
    return {
      label: `${strings.venueConfig_payment_serviceChargePercent} (%) `,
      value: '',
      size: 'medium',
      type: 'select',
      placeholder: `${strings.venueConfig_payment_serviceChargePercent} (%) `,
      field: 'serviceCharge',
      validators: [{ check: Validators.validateMandatoryField }],
      responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
      required: true,
      isMulti: false,
    };
  }
  return {
    label: `${strings.venueConfig_payment_serviceChargePercent} (%) `,
    value: '',
    size: 'medium',
    type: 'text',
    typeValue: 'number',
    variant: 'outlined',
    placeholder: '',
    field: 'serviceChargePercentage',
    validators: [{ check: Validators.validateMandatoryField }, { check: Validators.validateFloat, max: 100 }],
    responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
    required: true,
  };
};

const basicDetailFields = (strings: any, hourlyPricing: boolean): FormModel[] => {
  const fields: FormModel[] = [
    {
      label: strings.venueConfig_general_venue,
      value: '',
      size: 'medium',
      type: 'select',
      placeholder: strings.venueConfig_general_venue,
      field: 'venues',
      validators: [{ check: Validators.validateMandatoryField }],
      responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
      required: true,
      isMulti: false,
    },
    {
      label: strings.venueConfig_column_segment,
      value: '',
      size: 'medium',
      type: 'select',
      placeholder: strings.venueConfig_column_segment,
      field: 'eventTypeId',
      validators: [{ check: Validators.validateMandatoryField }],
      responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
      required: true,
      isMulti: false,
    },
    {
      label: strings.venueConfig_general_maxCapacity,
      value: '',
      size: 'medium',
      type: 'text',
      typeValue: 'number',
      variant: 'outlined',
      placeholder: '',
      field: 'maxBookingCapacity',
      validators: [{ check: Validators.validateMandatoryField }, { check: Validators.validateInt, max: 1000000 }],
      responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
      required: true,
    },
  ];

  if (!hourlyPricing) {
    fields.push({
      label: strings.venueConfig_general_minRevenue,
      value: '',
      size: 'medium',
      type: 'text',
      typeValue: 'number',
      variant: 'outlined',
      placeholder: '',
      field: 'minimumRevenue',
      validators: [{ check: Validators.validateMandatoryField }, { check: Validators.validateFloat }],
      responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
      required: true,
    });
  }

  fields.push(
    {
      label: strings.venueConfig_general_availability,
      value: '',
      size: 'medium',
      type: 'select',
      placeholder: strings.venueConfig_general_availability,
      field: 'available',
      validators: [],
      responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
      required: false,
      isMulti: false,
    },
    {
      label: strings.venueConfig_general_tentative,
      value: '',
      size: 'medium',
      type: 'select',
      placeholder: strings.venueConfig_general_tentative,
      field: 'tentativeBookingAllowed',
      validators: [],
      responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
      required: false,
      isMulti: false,
    },
    {
      label: strings.venueConfig_general_minDays,
      value: '',
      size: 'medium',
      type: 'text',
      typeValue: 'number',
      variant: 'outlined',
      placeholder: '',
      field: 'minimumDaysBeforeBooking',
      validators: [{ check: Validators.validateMandatoryField }, { check: Validators.validateInt, max: 300 }],
      responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
      required: true,
    },
    {
      label: strings.venueConfig_general_holdDays,
      value: '',
      size: 'medium',
      type: 'text',
      typeValue: 'number',
      variant: 'outlined',
      placeholder: '',
      field: 'holdDays',
      validators: [
        { check: Validators.validateMandatoryField },
        { check: Validators.validateInt, message: strings.invalidHoldDaysMsg, max: 1800 },
      ],
      responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
      required: true,
    },
    {
      label: strings.venueConfig_general_preLeadTime,
      value: '',
      size: 'medium',
      type: 'text',
      typeValue: 'number',
      variant: 'outlined',
      placeholder: '',
      field: 'preLeadTime',
      validators: [{ check: Validators.validateMandatoryField }, { check: Validators.validateInt, max: 1440 }],
      responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
      required: true,
      endAdornment: 'min',
    },
    {
      label: strings.venueConfig_general_postLeadTime,
      value: '',
      size: 'medium',
      type: 'text',
      typeValue: 'number',
      variant: 'outlined',
      placeholder: '',
      field: 'postLeadTime',
      validators: [{ check: Validators.validateMandatoryField }, { check: Validators.validateInt, max: 1440 }],
      responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
      required: true,
      endAdornment: 'min',
    },
    {
      label: strings.venueConfig_general_rfq,
      value: '',
      size: 'medium',
      type: 'select',
      placeholder: strings.venueConfig_general_rfq,
      field: 'onlyRFQ',
      validators: [{ check: Validators.validateMandatoryField }],
      responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
      required: true,
      isMulti: false,
    },
  );

  if (!hourlyPricing) {
    fields.push({
      label: strings.venueConfig_general_fixedHallRental,
      value: '',
      size: 'medium',
      type: 'text',
      typeValue: 'number',
      variant: 'outlined',
      placeholder: '',
      field: 'fixedHallRental',
      validators: [{ check: Validators.validateFloat }],
      responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
      required: false,
    });
  }

  fields.push({
    label: strings.hourlyPricing1,
    value: '',
    size: 'medium',
    type: 'select',
    placeholder: strings.hourlyPricing1,
    field: 'hourlyPricing',
    validators: [{ check: Validators.validateMandatoryField }],
    responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
    required: true,
    isMulti: false,
  });

  if (hourlyPricing) {
    fields.push({
      label: strings.pricePerHour,
      value: '',
      size: 'medium',
      type: 'text',
      typeValue: 'number',
      variant: 'outlined',
      placeholder: '',
      field: 'pricePerHour',
      validators: [{ check: Validators.validateMandatoryField }, { check: Validators.validateFloat, max: 1000000 }],
      responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
      required: true,
    });
  }

  return fields;
};

/** Payment fields (service charge, confirmation). Schedule UI is injected by the caller. */
export const paymentDetailFields = (
  strings: any,
  serviceChargeDropdown: boolean,
  paymentScheduleNode?: ReactNode,
): FormModel[] => {
  const fields: FormModel[] = [
    sectionHeader(strings.venueConfig_section_paymentDetails, 'venueConfig_section_paymentDetails'),
    serviceChargeField(strings, serviceChargeDropdown),
    {
      label: strings.venueConfig_payment_bookingConfirmation,
      value: '',
      size: 'medium',
      type: 'select',
      placeholder: strings.venueConfig_payment_bookingConfirmation,
      field: 'bookingConfirmationBy',
      validators: [{ check: Validators.validateMandatoryField }],
      responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
      required: true,
      isMulti: false,
    },
  ];

  if (paymentScheduleNode) {
    fields.push({
      label: '',
      value: '',
      type: 'custom',
      placeholder: '',
      field: 'configPaymentSchedule',
      validators: [],
      required: false,
      responsive: { xs: 12 },
      children: paymentScheduleNode,
    });
  }

  return fields;
};

/** @deprecated Use paymentDetailFields */
export const bulkPaymentDetailFields = paymentDetailFields;

export const GeneralForm = (
  strings: any,
  hourlyPricing: boolean,
  serviceChargeDropdown = false,
  paymentScheduleNode?: ReactNode,
): FormModel[] => [
  sectionHeader(strings.venueConfig_section_basicDetails, 'venueConfig_section_basicDetails'),
  ...basicDetailFields(strings, hourlyPricing),
  ...paymentDetailFields(strings, serviceChargeDropdown, paymentScheduleNode),
];
